import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ModalSheet } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { storeLocale } from "@/lib/i18n/locale-preference";
import { locales } from "@/lib/i18n/config";
import { getSellerLoginUrl } from "@/lib/auth/storeSlug";
import { useLogoutMutation } from "@/shared/services/useLogoutMutation";
import { useThemeStore, type ThemePref } from "@/shared/theme/useTheme";
import { useGlobalSearchQuery } from "@/shared/components/GlobalSearch/useGlobalSearchQuery";
import { GlobalSearchPanel } from "@/shared/components/GlobalSearch/GlobalSearchPanel";
import { formatCount } from "@/lib/formatting/number";
import { CartsSidebarIcon } from "./SidebarIcons";
import { usePrimaryNavSuppressed } from "./PageChromeContext";

type BottomNavTab = {
  /** Stable id used for customize (order/hidden) persistence. */
  key: string;
  href: string;
  label: string;
  /** The rail's word when `label` is too long for a fifth of a 320 px bar
   *  ("Dashboard" → "Home"). The menu and the aria-label keep `label`. */
  shortLabel?: string;
  isActive: boolean;
  showHint?: boolean;
  /** A number on the item (Orders: tier 1, "بانتظار قرارك" — the same
   *  `selectNeedsYouCount` the desktop sidebar badge shows). Omitted or 0:
   *  nothing drawn. */
  count?: number;
  icon: React.ReactNode;
};

type BottomNavIdentity = {
  storeName: string;
  initial: string;
  logoUrl: string | null;
  /** Account hub (profile, store switcher, theme, logout) — the desktop avatar. */
  href: string;
};

type BottomNavProps = {
  tabs: BottomNavTab[];
  identity: BottomNavIdentity;
  locale: string;
  /** Scopes the customize (order/hidden) preference + search per store. */
  storeSlug: string;
  /**
   * Opens the store share sheet. Rendered as a pinned action row in the expand
   * menu (next to Search) rather than as a `tab`: it has no route, so it must
   * never land in the 3-slot rail or the customizable destination list.
   */
  onShareStore: () => void;
};

type NavCustomize = { order: string[]; hidden: string[] };

const CUSTOMIZE_VERSION = 2;

function storageKey(storeSlug: string) {
  return `seller:nav-customize:v${CUSTOMIZE_VERSION}:${storeSlug}`;
}

function readCustomize(storeSlug: string): NavCustomize {
  if (typeof window === "undefined") return { order: [], hidden: [] };
  try {
    const raw = window.localStorage.getItem(storageKey(storeSlug));
    if (!raw) return { order: [], hidden: [] };
    const parsed = JSON.parse(raw) as Partial<NavCustomize>;
    return {
      order: Array.isArray(parsed.order) ? parsed.order.filter((k) => typeof k === "string") : [],
      hidden: Array.isArray(parsed.hidden) ? parsed.hidden.filter((k) => typeof k === "string") : [],
    };
  } catch {
    return { order: [], hidden: [] };
  }
}

/** Applies the saved order (unknown keys appended in default order) + hidden set. */
function applyCustomize(tabs: BottomNavTab[], c: NavCustomize) {
  const byKey = new Map(tabs.map((tab) => [tab.key, tab]));
  const seen = new Set<string>();
  const ordered: BottomNavTab[] = [];
  for (const k of c.order) {
    const tab = byKey.get(k);
    if (tab && !seen.has(k)) {
      ordered.push(tab);
      seen.add(k);
    }
  }
  for (const tab of tabs) {
    if (!seen.has(tab.key)) {
      ordered.push(tab);
      seen.add(tab.key);
    }
  }
  const hidden = new Set(c.hidden);
  const visible = ordered.filter((tab) => !hidden.has(tab.key));
  return { ordered, visible };
}

/**
 * Mobile bottom navigation (Trioty). A floating, bottom-anchored pill next to a
 * dedicated Search button. Collapsed it's a rail of the first 3 destinations +
 * a "More" toggle; tapping More grows the pill UPWARD into a full menu — headed
 * by the store identity + a Customize control, with the route-less actions
 * (Share store, Search) pinned at the bottom.
 * The menu order and hidden items are user-customizable and persisted per store.
 */
export function BottomNav({
  tabs,
  identity,
  locale,
  storeSlug,
  onShareStore,
}: BottomNavProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const logoutMutation = useLogoutMutation();
  const [expanded, setExpanded] = useState(false);
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState("");
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [customization, setCustomization] = useState(() => ({ storeSlug, value: readCustomize(storeSlug) }));
  // The storage key and customization share an owner through a store change.
  // Restore before commit so the previous store can never populate this key.
  if (customization.storeSlug !== storeSlug) {
    setCustomization({ storeSlug, value: readCustomize(storeSlug) });
  }
  const customize = customization.value;
  const barRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // A page-owned bottom bar (product edit's "تم الحفظ / معاينة") takes the
  // nav's place while it is mounted — the pill used to show above the bar.
  const navHidden = usePrimaryNavSuppressed();

  // Route-less account actions surfaced in the expand-menu footer, so a seller
  // can flip language/theme or sign out without drilling into Account settings.
  const themePref = useThemeStore((s) => s.pref);
  const setThemePref = useThemeStore((s) => s.setPref);
  const themeOptions: { value: ThemePref; label: string; icon: React.ReactNode }[] = [
    { value: "light", label: t(keys.common.themeLight), icon: <SunIcon /> },
    { value: "system", label: t(keys.common.themeSystem), icon: <MonitorIcon /> },
    { value: "dark", label: t(keys.common.themeDark), icon: <MoonIcon /> },
  ];

  const otherLocale = locale === "ar" ? "en" : "ar";
  const otherLocaleLabel =
    otherLocale === "ar"
      ? t(keys.common.languageArabic)
      : t(keys.common.languageEnglish);

  const handleSwitchLocale = () => {
    setExpanded(false);
    // Persist it: locale otherwise lives only in the URL, so the choice was
    // lost the next time the portal was opened at the bare host.
    storeLocale(otherLocale);
    const path = pathname ?? "/";
    const pattern = new RegExp(`^/(${locales.join("|")})(/|$)`);
    const nextPath = pattern.test(path)
      ? path.replace(pattern, `/${otherLocale}$2`)
      : `/${otherLocale}${path.startsWith("/") ? "" : "/"}${path}`;
    // Keep the query string + hash so page filters / open panels survive the
    // language switch (it's a locale change, not a navigation).
    navigate(`${nextPath}${search}${hash}`, { replace: true });
  };

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync({ storeSlug });
      setExpanded(false);
      navigate(getSellerLoginUrl(locale), { replace: true });
    } catch {
      // Logout failed (the mutation suppresses the global error toast): keep
      // the menu open so the button stays visible and actionable for a retry —
      // the seller is still authenticated on the current page. isPending resets
      // on rejection, so the button re-enables on its own.
    }
  };

  const { data: searchData, isFetching: searchFetching, error: searchError } =
    useGlobalSearchQuery({ storeSlug, q: query });
  const searchReady = query.trim().length >= 2;

  const openSearch = () => {
    setExpanded(false);
    setSearching(true);
  };
  const closeSearch = () => {
    setSearching(false);
    setQuery("");
  };

  // Focus the search input once the morph-open transition has started.
  useEffect(() => {
    if (!searching) return;
    const id = window.setTimeout(() => searchInputRef.current?.focus(), 60);
    return () => window.clearTimeout(id);
  }, [searching]);

  // Persist whenever the customization changes.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageKey(customization.storeSlug), JSON.stringify(customize));
    } catch {
      /* storage full / unavailable — ignore */
    }
  }, [customize, customization.storeSlug]);

  // Close the menu / search on a tap anywhere outside the bar.
  useEffect(() => {
    if (!expanded && !searching) return;
    const onDown = (e: PointerEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        setExpanded(false);
        closeSearch();
      }
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [expanded, searching]);

  const { ordered, visible } = useMemo(
    () => applyCustomize(tabs, customize),
    [tabs, customize],
  );

  // Collapsed rail (roundtable 2, B8): the first FOUR destinations, each
  // with its label under the icon — الرئيسية · الطلبات · المنتجات · العملاء —
  // then "المزيد" (≡). The same five words, in the same order, as the desktop
  // sidebar; icon-only tabs with ⇕ for "more" had to be learned.
  const railTabs = visible.slice(0, 4);
  // The "More" toggle lights up when the active route isn't one of the rail 3.
  const moreActive = !railTabs.some((tab) => tab.isActive);

  const setFullOrder = (nextOrder: string[]) =>
    setCustomization((state) => state.storeSlug === storeSlug
      ? { ...state, value: { ...state.value, order: nextOrder } }
      : state);
  const hideTab = (key: string) =>
    setCustomization((state) => state.storeSlug === storeSlug
      ? { ...state, value: {
          ...state.value,
          hidden: state.value.hidden.includes(key) ? state.value.hidden : [...state.value.hidden, key],
        } }
      : state);
  const showTab = (key: string) =>
    setCustomization((state) => state.storeSlug === storeSlug
      ? { ...state, value: { ...state.value, hidden: state.value.hidden.filter((k) => k !== key) } }
      : state);

  const railBtn =
    // Grow from the label's own width (`1 1 auto`), not five equal fifths:
    // at 320 px "Customers" needs more room than "More", and equal tabs split
    // it mid-word.
    "relative flex h-[52px] min-w-0 flex-[1_1_auto] flex-col items-center justify-center gap-0.5 rounded-[18px] px-1 transition-colors";
  // Rail labels never take an ellipsis ("Dashboa…", "الرئيس…" at 412 / 320
  // px): short words, a smaller size below 360 px, and a second line rather
  // than a clip for a customised tab with a long name.
  const railLabel =
    "max-w-full whitespace-normal text-center text-[10.5px] font-semibold leading-[1.15] tracking-[-0.01em] max-[359px]:text-[9.5px]";
  // Pinned action rows (Share store, Search) — same rhythm as the destination
  // links above them, but rendered as <button> since they have no route.
  const menuActionClass =
    "flex w-full items-center gap-3 rounded-[14px] px-3 py-2.5 text-start text-[13.5px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--soft)]";

  return (
    <>
      <nav
        hidden={navHidden}
        aria-label="Primary"
        className={[
          "pointer-events-none fixed inset-x-0 z-40 justify-center px-3 lg:hidden",
          navHidden ? "hidden" : "flex",
        ].join(" ")}
        style={{ bottom: "calc(14px + env(safe-area-inset-bottom, 0px))" }}
      >
        <div
          ref={barRef}
          className="flex w-full max-w-sm flex-col items-stretch gap-2"
        >
          {/* Search results — animate open above the bar (same feel as the menu). */}
          <div
            className={[
              "overflow-hidden rounded-[22px] transition-all duration-300 ease-out",
              searching && searchReady
                ? "pointer-events-auto max-h-[58vh] border border-[color:var(--card-br)] opacity-100 shadow-[0_12px_36px_rgba(0,0,0,0.16)]"
                : "max-h-0 border border-transparent opacity-0",
            ].join(" ")}
          >
            {/* pb-3: the last row ("عرض كل الطلبات") sat against the
                panel's bottom edge. */}
            <div className="max-h-[58vh] overflow-y-auto overscroll-contain bg-[color:var(--card)] pb-3 [scroll-padding-bottom:12px]">
              {searchFetching && !searchData ? (
                <p className="px-4 py-6 text-center text-[13px] text-[color:var(--text-muted)]">
                  {t(keys.globalSearch.loading)}
                </p>
              ) : searchError ? (
                <p role="alert" className="px-4 py-6 text-center text-[13px] text-[color:var(--text-muted)]">
                  {t(keys.globalSearch.error)}
                </p>
              ) : searchData ? (
                <GlobalSearchPanel
                  result={searchData}
                  locale={locale}
                  storeSlug={storeSlug}
                  q={query}
                  onNavigate={closeSearch}
                />
              ) : null}
            </div>
          </div>

          {/* Bottom row: the pill (rail ↔ menu) + Search button; the search field
              unfurls from the right (from the button) to cover the pill. */}
          <div className="relative flex items-end justify-center gap-2">
            <div className="pointer-events-auto min-w-0 flex-1 overflow-hidden rounded-[26px] border border-[color:var(--card-br)] bg-[color:var(--card)] shadow-[0_12px_36px_rgba(0,0,0,0.16)] sm:max-w-[340px]">
            {/* Expand menu — identity header, every visible destination, then the
                account actions. The whole column scrolls as one within 75vh, so
                on a short viewport the footer can't consume the height and leave
                destinations unreachable — everything stays in the scroll range. */}
            <div
              className={[
                "flex flex-col transition-all duration-300 ease-out",
                expanded
                  ? "max-h-[75vh] overflow-y-auto overscroll-contain opacity-100"
                  : "max-h-0 overflow-hidden opacity-0",
              ].join(" ")}
            >
              {/* Header: store identity (→ account hub) + customize control. */}
              <div className="flex shrink-0 items-center gap-2.5 px-3 pb-1.5 pt-3">
                <Link
                  to={identity.href}
                  onClick={() => setExpanded(false)}
                  className="flex min-w-0 flex-1 items-center gap-2.5 rounded-[10px] transition-colors hover:bg-[color:var(--soft)]"
                >
                  {identity.logoUrl ? (
                    <img
                      src={identity.logoUrl}
                      alt=""
                      className="h-8 w-8 shrink-0 rounded-[10px] border border-[color:var(--hair)] object-cover"
                    />
                  ) : (
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] text-[13px] font-bold"
                      style={{ background: "var(--accent-500)", color: "var(--on-accent)" }}
                    >
                      {identity.initial}
                    </span>
                  )}
                  <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-[color:var(--text-primary)]">
                    {identity.storeName}
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={() => setCustomizeOpen(true)}
                  aria-label={t(keys.seller.navCustomize)}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--soft)] hover:text-[color:var(--text-primary)]"
                >
                  <SlidersIcon />
                </button>
              </div>
              <div className="mx-3 my-1 h-px shrink-0 bg-[color:var(--hair)]" />

              <div className="flex flex-col gap-0.5 p-2 pt-1">
                {visible.map((tab) => (
                  <Link
                    key={tab.key}
                    to={tab.href}
                    onClick={() => setExpanded(false)}
                    aria-current={tab.isActive ? "page" : undefined}
                    className={[
                      "flex items-center gap-3 rounded-[14px] px-3 py-2.5 text-[13.5px] transition-colors",
                      tab.isActive
                        ? "font-semibold text-[color:var(--accent-500)]"
                        : "font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--soft)]",
                    ].join(" ")}
                    style={
                      tab.isActive
                        ? { background: "color-mix(in srgb, var(--accent-500) 10%, transparent)" }
                        : undefined
                    }
                  >
                    <span className="relative flex shrink-0">
                      {tab.icon}
                      {tab.showHint && !tab.count ? (
                        <span
                          className="absolute -end-0.5 -top-0.5 h-2 w-2 rounded-full"
                          style={{ background: "var(--gold-500)" }}
                        />
                      ) : null}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{tab.label}</span>
                    {tab.count ? (
                      <span
                        className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-bold"
                        style={{ background: "var(--accent-500)", color: "var(--on-accent)" }}
                      >
                        {formatCount(tab.count, locale)}
                      </span>
                    ) : null}
                  </Link>
                ))}
              </div>

              {/* Route-less actions — pinned at the bottom of the menu. Same
                  row rhythm as the destinations above (icon + label). */}
              <div className="mx-3 h-px shrink-0 bg-[color:var(--hair)]" />
              <div className="flex shrink-0 flex-col gap-0.5 p-2">
                <button
                  type="button"
                  onClick={() => {
                    setExpanded(false);
                    onShareStore();
                  }}
                  aria-label={t(keys.share.storeAria)}
                  className={menuActionClass}
                >
                  <span className="flex shrink-0">
                    <ShareNavIcon />
                  </span>
                  <span className="truncate">{t(keys.share.storeTitle)}</span>
                </button>

                {/* Account actions — theme, language, logout. Surfaced here so
                    the seller can switch without opening Account settings. */}
                <div className="mx-1 my-1 h-px bg-[color:var(--hair)]" />
                <div
                  role="radiogroup"
                  aria-label={t(keys.common.theme)}
                  className="flex w-full gap-0.5 rounded-[12px] border border-[color:var(--hair)] bg-[color:var(--soft)] p-[3px]"
                >
                  {themeOptions.map((option) => {
                    const isActive = themePref === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        aria-label={option.label}
                        title={option.label}
                        onClick={() => setThemePref(option.value)}
                        className={[
                          "flex h-8 flex-1 items-center justify-center rounded-[9px] transition-colors",
                          isActive
                            ? "bg-[color:var(--card)] text-[color:var(--text-primary)] shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                            : "text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]",
                        ].join(" ")}
                      >
                        {option.icon}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={handleSwitchLocale}
                  className={menuActionClass}
                >
                  <span className="flex shrink-0">
                    <GlobeIcon />
                  </span>
                  <span className="truncate">{otherLocaleLabel}</span>
                </button>
                <button
                  type="button"
                  onClick={() => void handleLogout()}
                  disabled={logoutMutation.isPending}
                  className="flex w-full items-center gap-3 rounded-[14px] px-3 py-2.5 text-start text-[13.5px] font-medium text-[color:var(--error-500)] transition-colors hover:bg-[color:var(--soft)] disabled:opacity-60"
                >
                  <span className="flex shrink-0">
                    <LogoutIcon />
                  </span>
                  <span className="truncate">{t(keys.common.logout)}</span>
                </button>
                {/* The logout mutation suppresses the global error toast, so a
                    failure would otherwise be silent — surface it here so the
                    seller knows they're still signed in and can retry. */}
                {logoutMutation.error ? (
                  <p
                    role="alert"
                    className="px-3 pb-1 text-[11.5px] text-[color:var(--error-500)]"
                  >
                    {t(keys.common.logoutError)}
                  </p>
                ) : null}
              </div>
            </div>

            {/* Rail — first 3 icons + More toggle. Collapses for the menu; fades
                out fast for search so the icons vanish before the field arrives. */}
            <div
              className={[
                "overflow-hidden transition-all ease-out",
                expanded
                  ? "max-h-0 opacity-0 duration-300"
                  : searching
                    ? "max-h-16 opacity-0 duration-150"
                    : "max-h-16 opacity-100 duration-300",
              ].join(" ")}
            >
              <div className="flex items-center justify-between gap-0.5 p-1">
                {railTabs.map((tab) => (
                  <Link
                    key={tab.key}
                    to={tab.href}
                    aria-label={
                      tab.count ? `${tab.label} (${formatCount(tab.count, locale)})` : tab.label
                    }
                    aria-current={tab.isActive ? "page" : undefined}
                    className={railBtn}
                    style={{
                      background: tab.isActive ? "var(--soft2)" : "transparent",
                      color: tab.isActive ? "var(--text-primary)" : "var(--text-muted)",
                    }}
                  >
                    {tab.icon}
                    <span aria-hidden="true" className={railLabel}>
                      {tab.shortLabel ?? tab.label}
                    </span>
                    {tab.count ? (
                      <span
                        aria-hidden="true"
                        className="absolute end-1 top-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10.5px] font-bold"
                        style={{ background: "var(--accent-500)", color: "var(--on-accent)" }}
                      >
                        {formatCount(tab.count, locale)}
                      </span>
                    ) : tab.showHint ? (
                      <span
                        className="absolute end-2.5 top-2.5 h-2 w-2 rounded-full"
                        style={{ background: "var(--gold-500)" }}
                      />
                    ) : null}
                  </Link>
                ))}
                <button
                  type="button"
                  aria-label={t(keys.seller.moreTitle)}
                  aria-expanded={expanded}
                  onClick={() => setExpanded(true)}
                  className={railBtn}
                  style={{
                    background: moreActive ? "var(--soft2)" : "transparent",
                    color: moreActive ? "var(--text-primary)" : "var(--text-muted)",
                  }}
                >
                  <MenuLinesIcon />
                  <span aria-hidden="true" className={railLabel}>
                    {t(keys.seller.moreTitle)}
                  </span>
                </button>
              </div>
            </div>
          </div>

            {/* Search field — unfurls horizontally from the right (from the button),
                anchored to the button's left edge so it grows toward the pill. */}
            <div
              aria-hidden={!searching}
              className={[
                "pointer-events-auto absolute inset-y-0 flex items-center gap-2 overflow-hidden rounded-[26px] border bg-[color:var(--card)] px-3.5 transition-all duration-300 ease-out",
                searching
                  ? "border-[color:var(--card-br)] opacity-100 shadow-[0_12px_36px_rgba(0,0,0,0.16)]"
                  : "pointer-events-none border-transparent opacity-0",
              ].join(" ")}
              style={{
                insetInlineEnd: "68px",
                width: searching ? "calc(100% - 68px)" : "0px",
              }}
            >
              <span className="flex shrink-0 text-[color:var(--text-muted)] [&_svg]:h-[18px] [&_svg]:w-[18px]">
                <SearchIcon />
              </span>
              <input
                ref={searchInputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") closeSearch();
                }}
                placeholder={t(keys.globalSearch.placeholder)}
                aria-label={t(keys.common.search)}
                tabIndex={searching ? 0 : -1}
                className="min-w-0 flex-1 bg-transparent text-[13px] text-[color:var(--text-primary)] outline-none placeholder:text-[11px] placeholder:text-[color:var(--text-muted)]"
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label={t(keys.common.cancel)}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
                >
                  <XIcon />
                </button>
              ) : null}
            </div>

            {/* Dedicated Search / close button — the last tab, right of the pill. */}
            {!expanded ? (
              <button
                type="button"
                onClick={searching ? closeSearch : openSearch}
                aria-label={searching ? t(keys.common.close) : t(keys.common.search)}
                className="pointer-events-auto relative z-10 flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--card)] text-[color:var(--text-muted)] shadow-[0_12px_36px_rgba(0,0,0,0.16)] transition-colors hover:text-[color:var(--text-primary)]"
              >
                {searching ? <XIcon /> : <SearchIcon />}
              </button>
            ) : null}
          </div>
        </div>
      </nav>

      <CustomizeSheet
        isOpen={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        items={ordered}
        hiddenKeys={customize.hidden}
        onReorder={setFullOrder}
        onHide={hideTab}
        onShow={showTab}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Customize sheet — tap a row to open it; arrows reorder, eye hides/shows
// ---------------------------------------------------------------------------

function CustomizeSheet({
  isOpen,
  onClose,
  items,
  hiddenKeys,
  onReorder,
  onHide,
  onShow,
}: {
  isOpen: boolean;
  onClose: () => void;
  items: BottomNavTab[];
  hiddenKeys: string[];
  onReorder: (order: string[]) => void;
  onHide: (key: string) => void;
  onShow: (key: string) => void;
}) {
  const t = useTranslations();
  const hidden = new Set(hiddenKeys);

  // Swap a row with its neighbour (up: -1, down: +1) within the full order.
  const move = (index: number, dir: -1 | 1) => {
    const to = index + dir;
    if (to < 0 || to >= items.length) return;
    const keys = items.map((it) => it.key);
    [keys[index], keys[to]] = [keys[to], keys[index]];
    onReorder(keys);
  };

  const rowBase =
    "flex items-center gap-2.5 border-b border-[color:var(--hair)] px-3.5 py-2.5 last:border-b-0 bg-[color:var(--card)]";
  const ctrlBtn =
    "flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--soft)] hover:text-[color:var(--text-primary)] disabled:pointer-events-none disabled:opacity-30";
  // Force the destination glyphs down to a compact size inside this list.
  const iconWrap = "shrink-0 text-[color:var(--text-secondary)] [&_svg]:h-[18px] [&_svg]:w-[18px]";

  return (
    <ModalSheet isOpen={isOpen} onClose={onClose} size="sm" surface="content">
      <div className="flex max-h-[80vh] flex-col">
        <div className="relative flex items-center justify-center px-4 py-4">
          <button
            type="button"
            onClick={onClose}
            aria-label={t(keys.common.cancel)}
            className="absolute start-4 flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--soft)] text-[color:var(--text-secondary)] transition-colors hover:text-[color:var(--text-primary)]"
          >
            <XIcon />
          </button>
          <h2 className="text-[15px] font-semibold text-[color:var(--text-primary)]">
            {t(keys.seller.navCustomize)}
          </h2>
        </div>

        <div className="overflow-y-auto px-4 pb-4">
          <ul className="overflow-hidden rounded-[16px] border border-[color:var(--card-br)]">
            {items.map((it, i) => {
              const isHidden = hidden.has(it.key);
              return (
                <li key={it.key} className={rowBase}>
                  <Link
                    to={it.href}
                    onClick={onClose}
                    aria-current={it.isActive ? "page" : undefined}
                    className="flex min-w-0 flex-1 items-center gap-2.5"
                  >
                    <span className={`${iconWrap} ${isHidden ? "opacity-45" : ""}`}>{it.icon}</span>
                    <span
                      className={[
                        "min-w-0 flex-1 truncate text-[14px] font-medium",
                        isHidden
                          ? "text-[color:var(--text-muted)] line-through"
                          : "text-[color:var(--text-primary)]",
                      ].join(" ")}
                    >
                      {it.label}
                    </span>
                  </Link>
                  <button
                    type="button"
                    onClick={() => (isHidden ? onShow(it.key) : onHide(it.key))}
                    aria-label={isHidden ? t(keys.common.show) : t(keys.common.hide)}
                    className={ctrlBtn}
                  >
                    {isHidden ? <EyeIcon /> : <EyeOffIcon />}
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    aria-label={t(keys.seller.navCustomize)}
                    className={ctrlBtn}
                  >
                    <ChevronUpIcon />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={i === items.length - 1}
                    aria-label={t(keys.seller.navCustomize)}
                    className={ctrlBtn}
                  >
                    <ChevronDownIcon />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </ModalSheet>
  );
}

// ---------------------------------------------------------------------------
// SVG icons used by the bottom nav
// ---------------------------------------------------------------------------

const NAV_ICON = {
  width: 22,
  height: 22,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

/** Compact glyphs for the theme segmented control (16px). */
const THEME_ICON = { ...NAV_ICON, width: 16, height: 16 };

function SunIcon() {
  return (
    <svg {...THEME_ICON}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function MonitorIcon() {
  return (
    <svg {...THEME_ICON}>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8" />
      <path d="M12 17v4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg {...THEME_ICON}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
  );
}

/** Language switch glyph (globe). */
function GlobeIcon() {
  return (
    <svg {...NAV_ICON}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" />
    </svg>
  );
}

/** Logout glyph (door + arrow). */
function LogoutIcon() {
  return (
    <svg {...NAV_ICON}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}

export function OrdersNavIcon() {
  return (
    <svg {...NAV_ICON}>
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
      <path d="M3 6h18" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}

export function TransactionsNavIcon() {
  return (
    <svg {...NAV_ICON}>
      <rect x="2" y="5" width="20" height="14" rx="2.5" />
      <path d="M2 10h20" />
      <path d="M6 15h3" />
      <path d="M13 15h5" />
    </svg>
  );
}

/** The store's own wallet — a banknote + coin, the same glyph the desktop
 *  sidebar's `PaymentsSidebarIcon` uses, so one destination has one mark.
 *  Deliberately not the card glyph `TransactionsNavIcon` uses for the online
 *  gateway transactions row above it. */
export function PaymentsNavIcon() {
  return (
    <svg {...NAV_ICON}>
      <rect x="2" y="6" width="20" height="12" rx="2.5" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 12h.01M18 12h.01" />
    </svg>
  );
}

export function ProductsNavIcon() {
  return (
    <svg {...NAV_ICON}>
      <path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" />
      <path d="m3 8 9 5 9-5" />
      <path d="M12 13v8" />
    </svg>
  );
}

export function HomeNavIcon() {
  return (
    <svg {...NAV_ICON} strokeWidth={1.9}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
    </svg>
  );
}

export function CustomersNavIcon() {
  return (
    <svg {...NAV_ICON}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    </svg>
  );
}

export function CategoriesNavIcon() {
  return (
    <svg {...NAV_ICON}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}

export function CouponsNavIcon() {
  return (
    <svg {...NAV_ICON}>
      <path d="M19 5 5 19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}

export function DiscountsNavIcon() {
  return (
    <svg {...NAV_ICON}>
      <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
      <path d="m15 9-6 6" />
      <path d="M9 9h.01" />
      <path d="M15 15h.01" />
    </svg>
  );
}

export function ReviewsNavIcon() {
  return (
    <svg {...NAV_ICON}>
      <path d="m12 3 2.9 5.9 6.1.9-4.5 4.3 1 6.1L12 17.8 6.5 20.2l1-6.1L3 9.8l6.1-.9L12 3Z" />
    </svg>
  );
}

export function StoreEditorNavIcon() {
  return (
    <svg {...NAV_ICON}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

export function SettingsNavIcon() {
  return (
    <svg {...NAV_ICON}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  );
}

/** Share store action glyph (three linked nodes). */
function ShareNavIcon() {
  return (
    <svg {...NAV_ICON}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
    </svg>
  );
}

/** Rail "More" toggle glyph (two stacked chevrons). */
function MenuLinesIcon() {
  return (
    <svg {...NAV_ICON}>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg {...NAV_ICON}>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

/** Customize control (sliders). */
function SlidersIcon() {
  return (
    <svg {...NAV_ICON} width={20} height={20}>
      <path d="M4 6h10" />
      <path d="M18 6h2" />
      <circle cx="16" cy="6" r="2" />
      <path d="M4 12h2" />
      <path d="M10 12h10" />
      <circle cx="8" cy="12" r="2" />
      <path d="M4 18h10" />
      <path d="M18 18h2" />
      <circle cx="16" cy="18" r="2" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg {...NAV_ICON} width={18} height={18}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function ChevronUpIcon() {
  return (
    <svg {...NAV_ICON} width={18} height={18} strokeWidth={2.1}>
      <path d="m6 15 6-6 6 6" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg {...NAV_ICON} width={18} height={18} strokeWidth={2.1}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg {...NAV_ICON} width={19} height={19}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg {...NAV_ICON} width={19} height={19}>
      <path d="M10.7 5.1A9.9 9.9 0 0 1 12 5c6.5 0 10 7 10 7a13.2 13.2 0 0 1-2.2 3" />
      <path d="M6.6 6.6A13.2 13.2 0 0 0 2 12s3.5 7 10 7a9.9 9.9 0 0 0 5.4-1.6" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Hook that builds the tab config — keeps AppShell clean
// ---------------------------------------------------------------------------

type UseBottomNavTabsInput = {
  metaHref: string;
  isMetaActive: boolean;
  dashboardHref: string;
  ordersHref: string;
  cartsHref: string;
  customersHref: string;
  transactionsHref: string;
  paymentsHref: string;
  productsHref: string;
  categoriesHref: string;
  couponsHref: string;
  discountsHref: string;
  freeShippingHref: string;
  reviewsHref: string;
  settingsHref: string;
  isDashboardActive: boolean;
  isOrdersActive: boolean;
  isCartsActive: boolean;
  isCustomersActive: boolean;
  isTransactionsActive: boolean;
  isPaymentsActive: boolean;
  isProductsActive: boolean;
  isCategoriesActive: boolean;
  isCouponsActive: boolean;
  isDiscountsActive: boolean;
  isFreeShippingActive: boolean;
  isReviewsActive: boolean;
  isSettingsActive: boolean;
  ordersShowHint?: boolean;
  /** Tier-1 count for the Orders item (same source as the sidebar badge). */
  ordersCount?: number;
  productsShowHint?: boolean;
  categoriesShowHint?: boolean;
  settingsShowHint?: boolean;
};

// The full destination set, mirroring the desktop sidebar. The collapsed rail
// shows the first three (Home, Orders, Products); everything else — Customers
// and Payments included — lives in the expand menu (users can reorder/hide via
// Customize). "Payments" and "Transactions" now open the SAME page on two
// different tabs (`/payments` and `/payments?tab=online`) — the rail has no
// sub-item concept, so both entries stay as two ways into the same page. Both
// ride on the one PAYMENT_VIEW gate (see the filter in AppShell). Their `key`s
// are unchanged so a seller's saved Customize order survives the merge.
// Settings → the account hub (`settingsHref` is `/account`, see AppShell),
// labelled "حسابي". Sellers who already customised their order keep it:
// `applyCustomize` replays the saved order over this list, so changing the
// default here only affects sellers who never customised.
export function useBottomNavTabs({
  metaHref,
  isMetaActive,
  dashboardHref,
  ordersHref,
  cartsHref,
  customersHref,
  transactionsHref,
  paymentsHref,
  productsHref,
  categoriesHref,
  couponsHref,
  discountsHref,
  freeShippingHref,
  reviewsHref,
  settingsHref,
  isDashboardActive,
  isOrdersActive,
  isCartsActive,
  isCustomersActive,
  isTransactionsActive,
  isPaymentsActive,
  isProductsActive,
  isCategoriesActive,
  isCouponsActive,
  isDiscountsActive,
  isFreeShippingActive,
  isReviewsActive,
  isSettingsActive,
  ordersShowHint,
  ordersCount,
  productsShowHint,
  categoriesShowHint,
  settingsShowHint,
}: UseBottomNavTabsInput): BottomNavTab[] {
  const t = useTranslations();

  return [
    {
      key: "dashboard",
      href: dashboardHref,
      label: t(keys.seller.dashboardTitle),
      shortLabel: t(keys.seller.navHomeShort),
      isActive: isDashboardActive,
      icon: <HomeNavIcon />,
    },
    {
      key: "orders",
      href: ordersHref,
      label: t(keys.orders.title),
      isActive: isOrdersActive,
      showHint: ordersShowHint,
      count: ordersCount && ordersCount > 0 ? ordersCount : undefined,
      icon: <OrdersNavIcon />,
    },
    {
      key: "products",
      href: productsHref,
      label: t(keys.seller.productsTitle),
      isActive: isProductsActive,
      showHint: productsShowHint,
      icon: <ProductsNavIcon />,
    },
    {
      key: "customers",
      href: customersHref,
      label: t(keys.customers.navLabel),
      isActive: isCustomersActive,
      icon: <CustomersNavIcon />,
    },
    {
      key: "carts",
      href: cartsHref,
      label: t(keys.carts.title),
      isActive: isCartsActive,
      icon: <CartsSidebarIcon />,
    },
    {
      key: "transactions",
      href: transactionsHref,
      label: t(keys.transactions.navLabel),
      isActive: isTransactionsActive,
      icon: <TransactionsNavIcon />,
    },
    {
      key: "payments",
      href: paymentsHref,
      label: t(keys.payments.navLabel),
      isActive: isPaymentsActive,
      icon: <PaymentsNavIcon />,
    },
    {
      key: "categories",
      href: categoriesHref,
      label: t(keys.seller.categoriesTitle),
      isActive: isCategoriesActive,
      showHint: categoriesShowHint,
      icon: <CategoriesNavIcon />,
    },
    {
      key: "coupons",
      href: couponsHref,
      label: t(keys.coupons.navLabel),
      isActive: isCouponsActive,
      icon: <CouponsNavIcon />,
    },
    {
      key: "discounts",
      href: discountsHref,
      label: t(keys.discounts.navLabel),
      isActive: isDiscountsActive,
      icon: <DiscountsNavIcon />,
    },
    {
      key: "freeShipping",
      href: freeShippingHref,
      label: t(keys.freeShipping.navLabel),
      isActive: isFreeShippingActive,
      icon: <DiscountsNavIcon />,
    },
    {
      key: "reviews",
      href: reviewsHref,
      label: t(keys.reviews.navLabel),
      isActive: isReviewsActive,
      icon: <ReviewsNavIcon />,
    },
    // The editor has no nav tab, on either viewport. It is reached by choosing
    // a design — Store Settings → Design → «عدّل» — because that is the act
    // that gives editing a subject. See the note in AppShell's Channels group.
    // The row opens the ACCOUNT hub (`/account`: store settings for those who
    // may open them, plus notifications, theme, language, password, logout),
    // so it is named "حسابي" for everyone. "إعدادات المتجر" names only the
    // store-settings route — an order moderator, who cannot open it, read
    // that label over her personal page. The key stays "settings" so saved
    // Customize orders survive.
    {
      key: "meta",
      href: metaHref,
      label: t(keys.meta.title),
      isActive: isMetaActive,
      icon: <ShareNavIcon />,
    },
    {
      key: "settings",
      href: settingsHref,
      label: t(keys.seller.navMyAccount),
      isActive: isSettingsActive,
      showHint: settingsShowHint,
      icon: <SettingsNavIcon />,
    },
  ];
}
